// Native PocketBase OTP auth (the only sign-in method — no password). The SDK's
// requestOTP/authWithOTP hit POST /api/collections/users/request-otp and
// /api/collections/users/auth-with-otp and, on success, persist the session
// token in pb.authStore (which the app shell subscribes to).

import type { RecordModel } from "pocketbase";
import { pb } from "./client.js";
import { sendJSON } from "./http.js";
import { parseError } from "./errors.js";

/** requestOtp emails a one-time code and returns the otpId to pair with it. */
export async function requestOtp(email: string): Promise<{ otpId: string }> {
  const res = await pb.collection("users").requestOTP(email.trim());
  return { otpId: res.otpId };
}

/**
 * verifyOtp exchanges an otpId + the emailed code for a session token, saved
 * into pb.authStore. The SDK passes the code as the `password` field.
 *
 * The 400 is re-tagged as `otp_invalid` here rather than in codeForStatus,
 * because only this route knows what a bare 400 means. PocketBase answers
 * every rejection — wrong code, expired code, unknown or already-spent otpId —
 * with the same codeless 400 `{"message":"Invalid or expired OTP"}` (a
 * deliberate anti-enumeration choice on its side), and the canonical envelope
 * this app's own routes use is absent. Without the re-tag the generic mapping
 * called it "internal" and told a user who simply mistyped their code that the
 * server had broken. Expired and wrong are indistinguishable on the wire, so
 * they share one message that covers both.
 */
export async function verifyOtp(otpId: string, code: string): Promise<void> {
  try {
    await pb.collection("users").authWithOTP(otpId, code.trim());
  } catch (err) {
    const parsed = parseError(err);
    if (parsed.status === 400 && parsed.code === "internal") {
      throw { ...parsed, code: "otp_invalid" };
    }
    throw err;
  }
}

/** SsoProvider is one enabled OAuth2 identity provider, for a sign-in button. */
export interface SsoProvider {
  name: string;
  displayName: string;
}

/**
 * listSsoProviders reports the OAuth2 providers enabled on the users
 * collection — non-empty exactly when an operator has configured enterprise
 * SSO. It never rejects: SSO buttons are an addition to a sign-in form that
 * must work without them, so an unreachable server, a 5xx, or SSO simply
 * being off all degrade to "no buttons" rather than blocking the email and
 * passkey paths behind an error the user cannot act on.
 */
export async function listSsoProviders(): Promise<SsoProvider[]> {
  try {
    const methods = await pb.collection("users").listAuthMethods();
    if (!methods.oauth2?.enabled) return [];
    return (methods.oauth2.providers ?? []).map((p) => ({
      name: p.name,
      // PocketBase leaves displayName blank for providers registered without
      // one; the raw name makes a far better button label than "Continue with".
      displayName: p.displayName?.trim() || p.name,
    }));
  } catch {
    return [];
  }
}

/**
 * The cancel key shared by every SSO attempt. Passing an explicit requestKey
 * is what makes cancellation possible at all — the SDK only installs an abort
 * handler on the OAuth2 flow when one is supplied — and reusing a single key
 * means a second provider click cleanly supersedes a first.
 */
const SSO_REQUEST_KEY = "sso";

/** How often the dismissal watch checks the popup, and see GRACE below. */
const POPUP_POLL_MS = 500;

/**
 * How long a closed popup must stay closed before we call it a dismissal.
 * PocketBase's own OAuth2 redirect page calls window.close() on itself the
 * moment the provider hands the code back, so on a SUCCESSFUL sign-in the
 * popup vanishes while the parent is still exchanging that code for a token.
 * Cancelling on the first closed poll would therefore abort real sign-ins.
 * The grace window only has to outlast that same-origin exchange; all it
 * costs a genuine dismissal is a few extra seconds of spinner.
 */
const POPUP_DISMISS_GRACE_MS = 4000;

/**
 * signInWithSso runs the SDK's popup OAuth2 flow for one provider and, on
 * success, returns the auth record (the session token is already persisted in
 * pb.authStore by then, same end state as OTP and passkeys).
 *
 * We open the popup ourselves via urlCallback rather than letting the SDK do
 * it, because the SDK keeps its popup handle private and has no notion of the
 * user closing the window: it waits on a realtime message that will never
 * arrive, leaving its promise pending forever and the caller's busy state
 * wedged. Watching the window we opened lets us turn that into a proper
 * cancellation (see isSsoCancel).
 */
export async function signInWithSso(provider: string): Promise<RecordModel> {
  let popup: Window | null = null;
  let closedAt = 0;

  const watch = setInterval(() => {
    if (!popup?.closed) {
      closedAt = 0;
      return;
    }
    if (closedAt === 0) closedAt = Date.now();
    // Aborting the controller behind SSO_REQUEST_KEY makes the SDK tear down
    // its realtime subscription and reject with an isAbort error.
    if (Date.now() - closedAt >= POPUP_DISMISS_GRACE_MS) pb.cancelRequest(SSO_REQUEST_KEY);
  }, POPUP_POLL_MS);

  let blocked = false;
  try {
    const auth = await pb.collection("users").authWithOAuth2({
      provider,
      requestKey: SSO_REQUEST_KEY,
      urlCallback: (url) => {
        popup = openSsoPopup(url);
        // A blocked popup would otherwise hang exactly like a dismissed one,
        // with no window to watch. Throwing here rejects the flow, but the SDK
        // rewraps it in a ClientResponseError that drops our code — so the
        // flag, checked below, is what carries the reason out.
        if (!popup) {
          blocked = true;
          throw new Error("SSO popup blocked");
        }
      },
    });
    return auth.record;
  } catch (err) {
    if (blocked) throw { code: "sso_popup_blocked", message: "" };
    throw err;
  } finally {
    clearInterval(watch);
    // The cast is load-bearing: popup is only ever assigned inside the
    // urlCallback closure, which control-flow analysis does not track, so TS
    // narrows it to `never` here.
    (popup as Window | null)?.close();
  }
}

/**
 * isSsoCancel reports whether a signInWithSso rejection means the user backed
 * out — they closed the provider window, or a second attempt superseded this
 * one — rather than anything failing. The SDK marks both with isAbort on its
 * ClientResponseError. Mirrors isWebAuthnCancel: cancels get no toast, because
 * the user already knows what they did.
 */
export function isSsoCancel(err: unknown): boolean {
  return !!err && typeof err === "object" && (err as { isAbort?: unknown }).isAbort === true;
}

/** openSsoPopup opens the provider's consent window, centred over the app. */
function openSsoPopup(url: string): Window | null {
  const w = Math.min(600, window.innerWidth);
  const h = Math.min(760, window.innerHeight);
  const left = window.screenX + (window.innerWidth - w) / 2;
  const top = window.screenY + (window.innerHeight - h) / 2;
  return window.open(url, "bino_sso", `width=${w},height=${h},left=${left},top=${top},resizable`);
}

/** signOut clears the persisted session (the shell reacts via authStore.onChange). */
export function signOut(): void {
  pb.authStore.clear();
}

/**
 * signOutEverywhere revokes every session for the caller server-side, including
 * the one making this call and any CLI tokens exchanged from API keys. It does
 * NOT clear the local store — the caller does that (via signOut) once the server
 * confirms, so a failed request leaves the user signed in rather than stranded
 * with a cleared store and live sessions elsewhere.
 */
export async function signOutEverywhere(): Promise<void> {
  await sendJSON<unknown>("/api/cloud/auth/sign-out-all", "POST", {});
}

/**
 * isOperator reports whether the signed-in user carries the operator flag (set
 * by a superuser in the PocketBase dashboard). It reads the cached auth record,
 * so it is only as fresh as the last authRefresh — the server independently
 * enforces the flag on every /v1/admin/* call, this merely decides whether to
 * show the Admin surface at all.
 */
export function isOperator(): boolean {
  return pb.authStore.record?.is_operator === true;
}

/** currentUser is the cached auth record, or null when signed out. */
export function currentUser(): { id: string; email: string; name: string } | null {
  const r = pb.authStore.record;
  if (!r) return null;
  return {
    id: r.id,
    email: (r.email as string | undefined) ?? "",
    name: (r.name as string | undefined) ?? "",
  };
}
